import type { GetEntitiesRequest } from '@backstage/catalog-client';
import type { Entity } from '@backstage/catalog-model';
import type { KnownKinds } from '@ipen-portal/plugin-common';

import {
  createExtensionBlueprint,
  createExtensionBlueprintParams,
  createExtensionDataRef,
} from '@backstage/frontend-plugin-api';

export type BreadcrumbDefinition<T extends Entity = Entity> = {
  filter: (e: Entity) => e is T;
  parent?: {
    /**
     * used to resolve the parent entity ref of this entity
     * @param current this entity
     */
    entityRefExtractor: (current: T) => null | string | undefined;
    /**
     * A fallback kind to use if the parent entity ref is not fully qualified'
     */
    fallbackKind?: KnownKinds;
    /**
     * if a parent entity can be resolved, this will be used as the subpath for the parent entities page tab
     */
    subPath?: string;
    /**
     * if a parent entity can be resolved, this will be used as a suffix on the breadcrumbs title
     */
    titleSuffix?: string;
  };
  root?: { link: string; title: string };
  siblingsExtractor?: (current: T) => GetEntitiesRequest | null;
};

const breadcrumbDefinitionRef =
  createExtensionDataRef<BreadcrumbDefinition>().with({
    id: 'ipen.breadcrumb-definition',
  });

export const BreadcrumbDefinitionBlueprint = createExtensionBlueprint({
  attachTo: { id: 'app/layout', input: 'breadcrumbDefinitions' },
  dataRefs: {
    breadcrumbPartDefinition: breadcrumbDefinitionRef,
  },
  defineParams: <T extends Entity>(params: BreadcrumbDefinition<T>) =>
    createExtensionBlueprintParams(
      params as unknown as BreadcrumbDefinition<Entity>,
    ),
  * factory(definition) {
    yield breadcrumbDefinitionRef(definition);
  },
  kind: 'breadcrumb-definition',
  output: [breadcrumbDefinitionRef],
});
